const formatCurrency = (
  value: number | undefined,
  options?: Intl.NumberFormatOptions
) => {
  if (!value) return ''
  const intl = new Intl.NumberFormat(undefined, {
    currency: 'GBP',
    ...(options ?? {}),
    style: 'currency'
  })
  return intl.format(value)
}

const formatDate = (value: Date, options?: Intl.DateTimeFormatOptions) => {
  return value.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    ...options
  })
}

const formatNumber = (
  value: number | undefined,
  options?: Intl.NumberFormatOptions
) => {
  if (!value && value !== 0) return ''
  const intl = new Intl.NumberFormat(undefined, { ...(options ?? {}) })
  return intl.format(value)
}

/**
 * Formats a URL for use with react-router-dom
 * @param  url:    A url string featuring `:<param>` sections
 * @param  params: Params to format into the URL
 * @return         the formatted URL
 */
function formatURL(url: string, params: { [key: string]: string }): string {
  let formatted = ''

  let index = 0
  while (index < url.length) {
    // if it's not a colon, just copy across
    if (url[index] !== ':') {
      formatted += url[index++]
      continue
    }

    // otherwise, keep going until we get to the end or a termination
    let end = ++index
    let optional = false
    while (end < url.length) {
      if (url[end] === '/') break
      if (url[end] === '?') {
        optional = true
        break
      }
      ++end
    }

    // we now have our key name, so extract it
    const name = url.substring(index, end)

    // if our key isn't optional but doesn't exist, throw
    if (optional === false && name in params === false)
      throw new Error(
        `Tried to replace ${name} but no value provided for it in params. Got: ${Object.keys(
          params
        ).join(', ')}`
      )

    if (
      optional === false &&
      (params[name] === null || params[name] === undefined)
    )
      throw new Error(
        `Tried to replace ${name} but provided null/undefined in params`
      )

    // either optional is false, and we know params[name] is valid, or name is in params
    // so add it to our formatted string
    if (
      optional === false ||
      (params[name] !== null && params[name] !== undefined)
    )
      formatted += params[name]

    // and set the index one past the end, ready to parse the next character
    index = end
  }

  return formatted
}

function toLocaleTitleCase(string: string | undefined) {
  if (!string) return ''
  if (typeof string !== 'string') return ''

  return string
    .split(' ')
    .filter((word) => word !== '')
    .map((word) => {
      if (word.length > 0) {
        return (
          word[0].toLocaleUpperCase() + word.substring(1).toLocaleLowerCase()
        )
      }
      return ''
    })
    .join(' ')
}

const BACKEND_URL = process.env.REACT_APP_STAGING
  ? 'https://ep-admin.cubane.org/'
  : process.env.NODE_ENV === 'development'
  ? 'http://127.0.0.1:3010/'
  : 'https://admin.eastern-procurement.co.uk/'

const API_URL = `${BACKEND_URL}api/`

const CAPTCHA_SITEKEY = '6LcKWgcrAAAAABixn33V8V9gN_S3FBnxX3pDO_RN'

/**
 * Checks if a URL is external (not same origin)
 * @param url The URL to check
 * @returns true if the URL is external
 */
function isExternalUrl(url: string): boolean {
  if (!url) return false
  
  try {
    // Handle relative URLs
    if (url.startsWith('/') || url.startsWith('#')) return false
    
    // Handle protocol-relative URLs
    if (url.startsWith('//')) {
      const currentProtocol = window.location.protocol
      url = currentProtocol + url
    }
    
    const urlObj = new URL(url, window.location.origin)
    return urlObj.origin !== window.location.origin
  } catch {
    // If URL parsing fails, assume it's internal
    return false
  }
}

/**
 * Checks if a URL points to a document file
 * @param url The URL to check
 * @returns true if the URL points to a document file
 */
function isDocumentFile(url: string): boolean {
  if (!url) return false
  
  const documentExtensions = [
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
    '.txt', '.rtf', '.odt', '.ods', '.odp', '.csv'
  ]
  
  const lowerUrl = url.toLowerCase()
  return documentExtensions.some(ext => lowerUrl.includes(ext))
}

/**
 * Determines if a link should open in a new tab
 * @param url The URL to check
 * @returns true if the link should open in a new tab
 */
function shouldOpenInNewTab(url: string): boolean {
  return isExternalUrl(url) || isDocumentFile(url)
}

/**
 * Gets the appropriate rel attribute for a link
 * @param url The URL to check
 * @param target The target attribute
 * @returns The rel attribute string
 */
function getRelAttribute(url: string, target?: string): string {
  if (target === '_blank' || shouldOpenInNewTab(url)) {
    return 'noopener noreferrer'
  }
  return ''
}

/**
 * Processes HTML content and automatically adds security attributes to external links
 * @param htmlContent The HTML content to process
 * @returns Processed HTML content with security attributes
 */
function processHtmlLinks(htmlContent: string): string {
  if (!htmlContent) return htmlContent
  
  // Create a temporary DOM element to parse and modify the HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlContent
  
  // Find all anchor tags
  const links = tempDiv.querySelectorAll('a')
  
  links.forEach(link => {
    const href = link.getAttribute('href')
    if (!href) return
    
    // Check if it should open in new tab
    if (shouldOpenInNewTab(href)) {
      link.setAttribute('target', '_blank')
      link.setAttribute('rel', 'noopener noreferrer')
    }
  })
  
  return tempDiv.innerHTML
}

type JSONArray = JSONValue[]
type JSONObject = { [key: string]: JSONValue | null }
type JSONValue = string | number | boolean | JSONArray | JSONObject

// copied from backend
type MenuItem = {
  name: string
  path?: string

  items: MenuItem[]
}

type MenuSerialised = {
  name: string
  items: MenuItem[]
}

type PageSerialised = {
  _id: string
  title: string
  parentId: string
  slug: string
  path: string
  metatitle?: string
  metadescription?: string
  permission?: string
  landingForUserType?: string
}

export {
  formatCurrency,
  formatDate,
  formatNumber,
  formatURL,
  toLocaleTitleCase,
  BACKEND_URL,
  API_URL,
  CAPTCHA_SITEKEY,
  isExternalUrl,
  isDocumentFile,
  shouldOpenInNewTab,
  getRelAttribute,
  processHtmlLinks
}

export type { JSONArray, JSONObject, JSONValue }
export type { MenuItem, MenuSerialised, PageSerialised }
